import React from 'react';
import {
  Building2, Star, Users, ContactRound, LineChart, Settings, ScrollText, Target, Flag,
  Database, AlertTriangle, Radar, LayoutDashboard, Wrench, FileText, Briefcase, Swords,
  Scale, FolderOpen, Archive, FilePlus, Upload, Inbox, Calculator, Download, Gauge,
  ListChecks, Map as MapIcon, MessageSquare, Store, Calendar, Network, SlidersHorizontal,
  Search, FlaskConical, Files, CircleUser, Puzzle, GitCompare, Circle,
  type LucideIcon,
} from 'lucide-react';
import { NavItem as NavItemType } from './useNavGroups';

interface NavItemProps {
  item: NavItemType;
  isActive: boolean;
  isCollapsed: boolean;
  onClick: () => void;
}

/**
 * NavItem — Individual navigation item in the left rail.
 *
 * Active state:
 * - 3px solid orange (#FF6600 via var(--color-brand)) left border
 * - White text + white icon
 * - bg-elevated background
 *
 * Icons: monochrome lucide-react, currentColor, 16px, strokeWidth 1.75 (K3 design gate,
 * 2026-10-08). Icons are NEVER orange — the leading accent bar owns the accent. This
 * replaced the old placeholder that used first-letter glyphs (which collided: F×3 "Firms/
 * Feature Flags/Fleet HUD", A×2 "All Users/Activity Log") and multi-color emoji (📊/⚙️,
 * the only colored pixels outside the accent). Keep a CI emoji grep so it never regresses.
 *
 * Badge counts: solid orange pill (bg-brand text-white)
 */
export default function NavItem({ item, isActive, isCollapsed, onClick }: NavItemProps) {
  const Icon = getIconForItem(item);
  return (
    <button
      onClick={onClick}
      className={`
        w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all
        ${isActive
          ? 'bg-[var(--color-bg-elevated)] text-white border-l-[3px] border-[var(--color-brand)]'
          : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] hover:text-white border-l-[3px] border-transparent'
        }
      `}
      title={isCollapsed ? item.label : undefined}
    >
      {/* Monochrome lucide icon — inherits currentColor (muted at rest, white on hover/active),
          never the accent. */}
      <span
        className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${isActive ? 'text-white' : 'text-[var(--color-text-muted)]'}`}
      >
        <Icon size={16} strokeWidth={1.75} aria-hidden="true" />
      </span>

      {!isCollapsed && (
        <>
          <span className="font-medium flex-1 text-left truncate">{item.label}</span>
          {item.badge !== undefined && item.badge > 0 && (
            <span className="bg-[var(--color-brand)] text-white text-xs px-2 py-0.5 rounded-full flex-shrink-0 min-w-[20px] text-center">
              {item.badge > 99 ? '99+' : item.badge}
            </span>
          )}
        </>
      )}

      {/* Badge for collapsed state */}
      {isCollapsed && item.badge !== undefined && item.badge > 0 && (
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-[var(--color-brand)] rounded-full flex items-center justify-center">
          <span className="text-[10px] text-white font-medium">
            {item.badge > 9 ? '9+' : item.badge}
          </span>
        </span>
      )}
    </button>
  );
}

/**
 * Map a nav item to a monochrome lucide icon. Admin-rail icons are the K3-gate
 * prescription (2026-10-08); other rails map to the nearest semantic icon, with a
 * neutral Circle as the default — never a first-letter glyph, never an emoji.
 */
const ICON_BY_ID: Record<string, LucideIcon> = {
  // ---- Admin rail (K3 prescription) ----
  'admin-firms': Building2,
  'admin-golden-cases': Star,
  'admin-all-users': Users,
  'analytics': LineChart,
  'settings': Settings,
  'activity-log': ScrollText,
  'admin-leads': Target,
  'admin-team-members': ContactRound,
  'admin-feature-flags': Flag,
  'admin-data-health': Database,
  'admin-error-logs': AlertTriangle,
  'fleet-hud': Radar,

  // ---- Work desks / role homes ----
  'work-desk': LayoutDashboard,
  'workdesk-admin': LayoutDashboard,
  'workdesk-analyst': LayoutDashboard,
  'workdesk-data-specialist': LayoutDashboard,
  'workdesk-accountant-internal': LayoutDashboard,
  'workdesk-accountant-external': LayoutDashboard,
  'workdesk-attorney': LayoutDashboard,
  'attorney-workdesk': LayoutDashboard,
  'accountant-workdesk': LayoutDashboard,
  'staff-dashboard': LayoutDashboard,
  'attorney-dashboard': LayoutDashboard,
  'attorney-home': LayoutDashboard,
  'accountant-home': LayoutDashboard,
  'tools-hub': Wrench,

  // ---- Attorney ----
  'attorney-portfolio': Briefcase,
  'attorney-war-room': Swords,
  'staff-war-room': Swords,
  'attorney-case': FolderOpen,
  'attorney-case-documents': Files,
  'case-documents': Files,
  'attorney-overlay': SlidersHorizontal,
  'settlement-intel': Scale,
  'game-theory': Puzzle,
  'roadmap': MapIcon,
  'bi-timeline': Calendar,
  'bi-timeline-picker': Calendar,

  // ---- Generators / deliverables ----
  'generators': FileText,
  'gen-demand-letter': FileText,
  'gen-demand-letter-picker': FileText,
  'gen-euo-prep': FileText,
  'gen-euo-prep-picker': FileText,
  'gen-proof-of-loss': FileText,
  'gen-proof-of-loss-picker': FileText,
  'gen-ror-analysis': FileText,
  'gen-ror-analysis-picker': FileText,
  'gen-ror-response': FileText,
  'gen-ror-response-picker': FileText,
  'export-center-internal': Download,
  'export-center-external': Download,

  // ---- Cases / intake ----
  'case-status': FolderOpen,
  'closed-cases': Archive,
  'new-claim': FilePlus,
  'new-claim-legacy': FilePlus,
  'new-case-v2': FilePlus,
  'unified-upload': Upload,
  'bulk-upload': Upload,
  'batch-processing': Upload,

  // ---- Queues / review ----
  'work-queue': Inbox,
  'transaction-review-queue': Inbox,
  'data-specialist-queue': Inbox,
  'data-review': Inbox,
  'anomaly-review': Search,
  'cleanup-validation': ListChecks,
  'cleanup-validation-job': ListChecks,
  'issue-tracker': ListChecks,
  'issue-detail': ListChecks,

  // ---- Reconciliation / engines ----
  'recon-workbench': Calculator,
  'reconciliation-external': Calculator,
  'engine-review-internal': Gauge,
  'engine-review-external': Gauge,
  'engine-review-detail-internal': Gauge,
  'engine-review-detail-external': Gauge,
  'engine-review-edit-internal': Gauge,
  'engine-audit': Gauge,
  'methodology-internal': FlaskConical,
  'analysis-tools-internal': FlaskConical,
  'analysis-stage': FlaskConical,

  // ---- Reports / intel / misc ----
  'report-detail': FileText,
  'accountant-report-view': FileText,
  'accountant-reports': FileText,
  'compare-reports': GitCompare,
  'narrator': MessageSquare,
  'accountant-narrator': MessageSquare,
  'accountant-comments': MessageSquare,
  'vendor-intel': Store,
  'adjuster-analyzer': Search,
  'orchestrator': Network,
  'pattern-admin': SlidersHorizontal,
  'accountant-profile': CircleUser,
  'accountant-analysis-tab': LineChart,
};

function getIconForItem(item: NavItemType): LucideIcon {
  return ICON_BY_ID[item.id] || Circle;
}
